import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowLeft, AlertTriangle, WifiOff, ServerOff, LucideIcon } from 'lucide-react';

export type ErrorType = 'service_unavailable' | 'network_error' | 'not_found' | 'generic';

interface ErrorStatusPageProps {
    type?: ErrorType;
    title?: string;
    message?: string;
    onRetry?: () => void;
    showBackButton?: boolean;
}

const ERROR_CONFIG: Record<ErrorType, { title: string; message: string; Icon: LucideIcon }> = {
    service_unavailable: {
        title: 'Service Temporarily Unavailable',
        message: 'We\'re experiencing technical difficulties. Our team has been notified. Please try again in a few moments.',
        Icon: ServerOff,
    },
    network_error: {
        title: 'Connection Issue',
        message: 'We couldn\'t connect to the server. Please check your internet connection and try again.',
        Icon: WifiOff,
    },
    not_found: {
        title: 'Not Found',
        message: 'The requested item could not be found. It may have been deleted or moved.',
        Icon: AlertTriangle,
    },
    generic: {
        title: 'Something Went Wrong',
        message: 'We encountered an unexpected issue. Please try again, or contact support if the problem persists.',
        Icon: AlertTriangle,
    },
};

export const ErrorStatusPage: React.FC<ErrorStatusPageProps> = ({
    type = 'generic',
    title,
    message,
    onRetry,
    showBackButton = true,
}) => {
    const navigate = useNavigate();

    const config = ERROR_CONFIG[type];
    const displayTitle = title || config.title;
    const displayMessage = message || config.message;
    const Icon = config.Icon;

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 animate-fade-in">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-6">
                <Icon size={28} className="text-neutral-500" />
            </div>

            {/* Title */}
            <h1 className="text-xl font-semibold text-neutral-900 mb-2 text-center">
                {displayTitle}
            </h1>

            {/* Message */}
            <p className="text-sm text-neutral-600 mb-8 text-center max-w-md">
                {displayMessage}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-3">
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="btn-primary flex items-center gap-2"
                    >
                        <RefreshCw size={16} />
                        Try Again
                    </button>
                )}

                {showBackButton && (
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost flex items-center gap-2"
                    >
                        <ArrowLeft size={16} />
                        Go Back
                    </button>
                )}
            </div>
        </div>
    );
};

export default ErrorStatusPage;
